import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { SectionEditProps } from '@/components/onboarding/OnboardingTimeline';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { ApiError } from '@/lib/api';
import { CAPS } from '@/lib/kbCaps';
import {
  diffForUpdate,
  useTenantProfileForm,
  type TenantProfileFormValues,
} from '@/hooks/useTenantProfileForm';
import { tenantProfileService } from '@/services/tenantProfileService';

export function InfoSection({ editing = false, onEditDone }: SectionEditProps = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, markStepCompleted, refresh } = useOnboardingState();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useTenantProfileForm({
    initial: {
      business_name: profile?.business_name || '',
      owner_name: profile?.owner_name || user?.name || '',
      language: profile?.language,
      owner_name_fallback: user?.name ?? '',
    },
  });

  async function onSubmit(values: TenantProfileFormValues) {
    setSubmitError(null);
    try {
      const diff = diffForUpdate(values, profile ?? {});
      if (Object.keys(diff).length > 0) {
        await tenantProfileService.update(diff);
      }
      if (editing) {
        // F063 — local edit of a completed step: persist the data only;
        // never touch the funnel pointer.
        await refresh();
        onEditDone?.();
      } else {
        await markStepCompleted('info');
        await refresh();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message || t('common.error_generic'));
      } else {
        setSubmitError(t('common.error_generic'));
      }
    }
  }

  return (
    <div data-testid="section-info">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <FormField
            control={form.control}
            name="business_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('onboarding.step1.business_name')}</FormLabel>
                <FormControl>
                  <Input
                    className="h-11"
                    autoComplete="organization"
                    maxLength={CAPS.business_name}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="owner_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('onboarding.step1.owner_name')}</FormLabel>
                <FormControl>
                  <Input
                    className="h-11"
                    autoComplete="name"
                    maxLength={CAPS.owner_name}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t('onboarding.step1.language')}</FormLabel>
                <div className="flex flex-col gap-2">
                  {(['pt-BR', 'pt-PT'] as const).map((lang) => (
                    <label
                      key={lang}
                      className="flex items-center gap-3 min-h-11 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-muted/60 transition-colors"
                    >
                      <input
                        type="radio"
                        value={lang}
                        checked={field.value === lang}
                        onChange={() => field.onChange(lang)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{t(`onboarding.step1.language_${lang}`)}</span>
                    </label>
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {submitError && (
            <div role="alert" className="text-sm text-destructive">
              {submitError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full sm:w-auto h-11"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? t('common.loading') : t('onboarding.step1.submit')}
          </Button>
        </form>
      </Form>
    </div>
  );
}
